import styled from '@emotion/styled';
import { findIconDefinition, IconName, library } from '@fortawesome/fontawesome-svg-core';
import { faAngleDown, fas } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as React from 'react';
import { Link } from 'react-router';
import { TagBadgeWidget } from './TagBadgeWidget';

library.add(fas);

const ICON_CHILD_INDENT_OFFSET = 22;
const DISCLOSURE_GUTTER = 22;

const hasCollapsibleItems = (items: TreeListItem[]): boolean =>
  items.some((item) => (item.collapsible && !!item.children?.length) || hasCollapsibleItems(item.children ?? []));

export enum TreeListTone {
  DEFAULT = 'default',
  HEADING = 'heading',
  GROUP = 'group'
}

export interface TreeListItem {
  active?: boolean;
  children?: TreeListItem[];
  collapsible?: boolean;
  defaultExpanded?: boolean;
  key: string;
  label: string;
  icon?: string;
  level?: number;
  onClick?: () => void;
  tag?: string;
  tagVersion?: string;
  to?: string;
  tone?: TreeListTone;
}

export interface TreeListWidgetProps {
  className?: string;
  items: TreeListItem[];
  listRef?: React.Ref<HTMLDivElement>;
  highlight?: string;
}

export const TreeListWidget: React.FC<TreeListWidgetProps> = (props) => {
  if (props.items.length === 0) {
    return null;
  }

  const indentOffset = hasCollapsibleItems(props.items) ? DISCLOSURE_GUTTER : 0;

  return (
    <S.List ref={props.listRef} className={props.className}>
      {props.items.map((item) => (
        <TreeNode key={`${item.key}-0`} item={item} level={0} indentOffset={indentOffset} highlight={props.highlight} />
      ))}
    </S.List>
  );
};

const TreeNode: React.FC<{ item: TreeListItem; indentOffset: number; level: number; highlight?: string }> = ({
  item,
  indentOffset,
  level,
  highlight
}) => {
  const childrenId = React.useId();
  const matchIndex = highlight ? item.label.toLocaleLowerCase().indexOf(highlight.toLocaleLowerCase()) : -1;
  const label =
    matchIndex < 0 ? (
      item.label
    ) : (
      <>
        {item.label.slice(0, matchIndex)}
        <S.Match>{item.label.slice(matchIndex, matchIndex + highlight!.length)}</S.Match>
        {item.label.slice(matchIndex + highlight!.length)}
      </>
    );
  const hasChildren = (item.children?.length ?? 0) > 0;
  const resolvedLevel = item.level ?? level;
  const resolvedTone = item.tone ?? TreeListTone.DEFAULT;
  const isCollapsible = hasChildren && item.collapsible;
  const iconDefinition = item.icon ? findIconDefinition({ prefix: 'fas', iconName: item.icon as IconName }) : undefined;
  const [isExpanded, setIsExpanded] = React.useState(() => item.defaultExpanded ?? false);
  const toggleExpanded = React.useCallback(() => {
    setIsExpanded((value) => !value);
  }, []);

  React.useEffect(() => {
    if (item.defaultExpanded !== undefined) {
      setIsExpanded(item.defaultExpanded);
    }
  }, [item.defaultExpanded, item.key]);

  const isDisclosure = isCollapsible && !item.to && !item.onClick;
  const itemContent = item.to ? (
    <S.LinkItem
      level={resolvedLevel}
      indentOffset={indentOffset}
      tone={resolvedTone}
      active={item.active}
      inCollapsible={isCollapsible}
      to={item.to}
      data-active={item.active}
    >
      <S.ItemContent>
        {iconDefinition ? <S.ItemIcon icon={iconDefinition} aria-hidden="true" /> : null}
        <span>{label}</span>
        {item.tag ? <TagBadgeWidget sinceVersion={item.tagVersion}>{item.tag}</TagBadgeWidget> : null}
      </S.ItemContent>
    </S.LinkItem>
  ) : item.onClick || isCollapsible ? (
    <S.ButtonItem
      level={resolvedLevel}
      indentOffset={indentOffset}
      tone={resolvedTone}
      active={item.active}
      inCollapsible={isCollapsible}
      data-active={item.active}
      type="button"
      aria-expanded={isDisclosure ? isExpanded : undefined}
      aria-controls={isDisclosure ? childrenId : undefined}
      onClick={item.onClick ?? toggleExpanded}
    >
      <S.ItemContent>
        {isDisclosure ? <S.DisclosureIcon expanded={isExpanded} icon={faAngleDown} /> : null}
        {iconDefinition ? <S.ItemIcon icon={iconDefinition} aria-hidden="true" /> : null}
        <span>{label}</span>
        {item.tag ? <TagBadgeWidget sinceVersion={item.tagVersion}>{item.tag}</TagBadgeWidget> : null}
      </S.ItemContent>
    </S.ButtonItem>
  ) : (
    <S.GroupLabel
      level={resolvedLevel}
      indentOffset={indentOffset}
      tone={resolvedTone}
      active={item.active}
      inCollapsible={isCollapsible}
      data-active={item.active}
    >
      <S.ItemContent>
        {iconDefinition ? <S.ItemIcon icon={iconDefinition} aria-hidden="true" /> : null}
        <span>{label}</span>
        {item.tag ? <TagBadgeWidget sinceVersion={item.tagVersion}>{item.tag}</TagBadgeWidget> : null}
      </S.ItemContent>
    </S.GroupLabel>
  );

  return (
    <>
      {isCollapsible && !isDisclosure ? (
        <S.CollapsibleRow active={item.active} data-active={item.active}>
          <S.ToggleButton
            type="button"
            active={item.active}
            $indent={10 + resolvedLevel * 16 + indentOffset - DISCLOSURE_GUTTER}
            aria-controls={childrenId}
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${item.label}`}
            onClick={(event) => {
              event.stopPropagation();
              toggleExpanded();
            }}
          >
            <S.ToggleIcon expanded={isExpanded} icon={faAngleDown} />
          </S.ToggleButton>
          {itemContent}
        </S.CollapsibleRow>
      ) : (
        itemContent
      )}
      {hasChildren ? (
        <div id={childrenId} hidden={Boolean(isCollapsible && !isExpanded)}>
          {item.children!.map((child) => (
            <TreeNode
              key={`${child.key}-${level + 1}`}
              indentOffset={indentOffset + (iconDefinition ? ICON_CHILD_INDENT_OFFSET : 0)}
              item={child}
              highlight={highlight}
              level={level + 1}
            />
          ))}
        </div>
      ) : null}
    </>
  );
};

namespace S {
  export const Match = styled.mark`
    background: ${(p) => p.theme.header.searchHighlightBackground};
    color: inherit;
    border-radius: 2px;
  `;

  const toneCss = (tone: TreeListTone | undefined, theme: any) => {
    if (tone === TreeListTone.GROUP) {
      return {
        color: theme.text.heading,
        fontSize: '14px',
        fontWeight: 600,
        opacity: 1
      };
    }

    if (tone === TreeListTone.HEADING) {
      return {
        color: theme.text.heading,
        fontSize: 'inherit',
        fontWeight: 600,
        opacity: 1
      };
    }

    return {
      color: theme.text.paragraph,
      fontSize: '14px',
      fontWeight: 400,
      opacity: 1
    };
  };

  const itemCss = ({
    indentOffset,
    level,
    tone,
    theme
  }: {
    indentOffset: number;
    level: number;
    tone?: TreeListTone;
    theme?: any;
  }) => ({
    color: toneCss(tone, theme).color,
    fontSize: toneCss(tone, theme).fontSize,
    fontWeight: toneCss(tone, theme).fontWeight,
    opacity: toneCss(tone, theme).opacity,
    paddingLeft: `${10 + level * 16 + indentOffset}px`
  });

  export const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
  `;

  export const ButtonItem = styled.button<{
    active?: boolean;
    indentOffset: number;
    inCollapsible?: boolean;
    level: number;
    tone?: TreeListTone;
  }>`
    appearance: none;
    background: transparent;
    border: 0;
    color: ${(p) => itemCss(p).color};
    cursor: pointer;
    font: inherit;
    font-size: var(--tree-list-font-size, ${(p) => itemCss(p).fontSize});
    font-weight: ${(p) => itemCss(p).fontWeight};
    opacity: ${(p) => itemCss(p).opacity};
    text-align: left;
    padding: 7px 10px;
    padding-left: ${(p) => itemCss(p).paddingLeft};
    border-radius: 10px;
    transition: transform 0.2s;
    text-decoration: none;
    display: block;
    width: 100%;

    background: ${(p) => (p.active && !p.inCollapsible ? p.theme.header.dropdownSelected : 'transparent')};
    color: ${(p) => (p.active ? 'white' : itemCss(p).color)};
    opacity: ${(p) => (p.active ? 1 : itemCss(p).opacity)};

    &:hover {
      transition:
        background 0.2s,
        color 0.2s,
        transform 0.2s;
      background: ${(p) =>
        p.active ? (p.inCollapsible ? 'transparent' : p.theme.header.dropdownSelected) : p.theme.header.dropdownHover};
    }
  `;

  export const LinkItem = styled(Link)<{
    active?: boolean;
    indentOffset: number;
    inCollapsible?: boolean;
    level: number;
    tone?: TreeListTone;
  }>`
    appearance: none;
    background: transparent;
    border: 0;
    color: ${(p) => itemCss(p).color};
    cursor: pointer;
    font: inherit;
    font-size: var(--tree-list-font-size, ${(p) => itemCss(p).fontSize});
    font-weight: ${(p) => itemCss(p).fontWeight};
    opacity: ${(p) => itemCss(p).opacity};
    text-align: left;
    padding: 7px 10px;
    padding-left: ${(p) => itemCss(p).paddingLeft};
    border-radius: 10px;
    transition: transform 0.2s;
    text-decoration: none;
    display: block;

    background: ${(p) => (p.active && !p.inCollapsible ? p.theme.header.dropdownSelected : 'transparent')};
    color: ${(p) => (p.active ? 'white' : itemCss(p).color)};
    opacity: ${(p) => (p.active ? 1 : itemCss(p).opacity)};

    &:hover {
      transition:
        background 0.2s,
        color 0.2s,
        transform 0.2s;
      background: ${(p) =>
        p.active ? (p.inCollapsible ? 'transparent' : p.theme.header.dropdownSelected) : p.theme.header.dropdownHover};
      text-decoration: none;
    }
  `;

  export const GroupLabel = styled.div<{
    active?: boolean;
    indentOffset: number;
    inCollapsible?: boolean;
    level: number;
    tone?: TreeListTone;
  }>`
    display: block;
    width: 100%;
    padding: 8px 10px;
    padding-left: ${(p) => itemCss(p).paddingLeft};
    color: ${(p) => (p.active ? 'white' : itemCss(p).color)};
    font-weight: ${(p) => itemCss(p).fontWeight};
    font-size: var(--tree-list-font-size, ${(p) => itemCss(p).fontSize});
    opacity: ${(p) => (p.active ? 1 : itemCss(p).opacity)};
  `;

  export const ItemContent = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;
  `;

  export const ItemIcon = styled(FontAwesomeIcon)`
    color: ${(p) => p.theme.text.special};
    flex: 0 0 14px;
    font-size: 13px;
    opacity: 0.95;
    width: 14px;
  `;

  export const CollapsibleRow = styled.div<{ active?: boolean }>`
    position: relative;
    min-width: 0;
    border-radius: 10px;
    background: ${(p) => (p.active ? p.theme.header.dropdownSelected : 'transparent')};
  `;

  export const ToggleButton = styled.button<{ active?: boolean; $indent: number }>`
    appearance: none;
    position: absolute;
    left: ${(p) => p.$indent - 4}px;
    top: 50%;
    transform: translateY(-50%);
    display: grid;
    place-items: center;
    background: transparent;
    border: 0;
    border-radius: 8px;
    color: ${(p) => p.theme.text.paragraph};
    cursor: pointer;
    flex: 0 0 auto;
    height: 32px;
    padding: 0;
    opacity: 0.7;
    transition: opacity 0.2s;
    width: 22px;

    &:hover {
      transition:
        background 0.2s,
        color 0.2s,
        opacity 0.2s;
      background: ${(p) => p.theme.header.dropdownHover};
      opacity: 1;
    }

    color: ${(p) => (p.active ? 'white' : p.theme.text.paragraph)};
    opacity: 1;

    ${(p) =>
      p.active
        ? `
      &:hover {
        background: transparent;
      }
    `
        : ''}
  `;

  export const ToggleIcon = styled(FontAwesomeIcon)<{ expanded: boolean }>`
    font-size: 12px;
    width: 14px;
    flex: 0 0 14px;
    transition: transform 0.2s ease;
    transform: ${(p) => (p.expanded ? 'rotate(0deg)' : 'rotate(-90deg)')};
  `;

  export const DisclosureIcon = styled(ToggleIcon)`
    margin-left: -${DISCLOSURE_GUTTER}px;
  `;
}
